import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Typography,
} from '@mui/material';
import {toast} from 'react-toastify';
import ConfirmModal from '../pieces/modals/confirmPopup';
import {CustomInput, FloatingLabel} from '../pieces/inputs/customInputComponent';
import {
  buildAiNeedToVerifyInspectionUrl,
  linkCallToInspection,
  searchInspectionsForCall,
} from '../utils/aiCallSession';
import {COLORS} from '../styles/styleConstants';

/** Below this the query matches most of the database, so searching is pointless. */
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 350;

/**
 * The dialer hands over the AI's lookup result as a raw JSON string. Turn it into a sentence an
 * agent can read; anything unrecognised is dropped, since the subtitle already says there was no
 * match. A non-JSON answer is free text from the AI and is shown as-is.
 */
const describeAiAnswer = (answer) => {
  if (!answer) {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(answer);
  } catch (e) {
    return answer;
  }
  if (!parsed || typeof parsed !== 'object') {
    return String(answer);
  }
  const parts = [];
  const count = Number(parsed.case_match_count) || 0;
  if (parsed.case_lookup_status === 'multiple_matches') {
    parts.push(count > 1
      ? `The AI found ${count} possible jobs and could not tell which one this is.`
      : 'The AI found more than one possible job and could not tell which one this is.');
  } else if (parsed.case_lookup_status === 'no_match' || parsed.case_lookup_status === 'not_found') {
    parts.push('The AI found no job for this caller.');
  }
  if (parsed.inspection_zip_unavailable === true) {
    parts.push('The caller\'s ZIP code was not available.');
  }
  return parts.length ? parts.join(' ') : null;
};

/**
 * Shown after an agent accepts a call the AI could not tie to a job.
 *
 * The call is already connected at this point — this is only about identifying the property, so
 * the dialog never blocks the conversation and can be closed without linking. The call stays
 * recorded as unmatched either way and can be picked up later from the Unmatched Calls view.
 *
 * Seeded with the address because on an inbound call it is usually the only identifying detail
 * the dialer captured; the phone number arrives empty.
 */
const AiCallInspectionSearchDialog = ({open, call, onClose}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkingJobNumber, setLinkingJobNumber] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const requestIdRef = useRef(0);

  // Seed from the call each time the dialog opens, then leave the agent's edits alone.
  useEffect(() => {
    if (open) {
      setQuery(call?.address || '');
      setResults([]);
      setHasSearched(false);
      setLinkingJobNumber(null);
    }
  }, [open, call?.address]);

  const runSearch = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    // Only the newest request may write results; a slow earlier one must not overwrite them.
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setSearching(true);
    const found = await searchInspectionsForCall(trimmed);
    if (requestIdRef.current === requestId) {
      setResults(found);
      setHasSearched(true);
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timer = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, query, runSearch]);

  const handleLink = async (row) => {
    if (!call?.aiCallId) {
      toast('This call cannot be linked - no call id was provided.', {type: 'error'});
      return;
    }
    setLinkingJobNumber(row.jobNumber);
    const result = await linkCallToInspection({
      aiCallId: call.aiCallId,
      jobNumber: row.jobNumber,
    });
    setLinkingJobNumber(null);
    if (!result.ok) {
      toast(result.error || 'Could not link this call.', {type: 'error'});
      return;
    }
    toast(`Call linked to job ${row.jobNumber}.`, {type: 'success'});
    onClose();
    const url = buildAiNeedToVerifyInspectionUrl(row.jobId);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const renderResult = (row) => (
    <Box
      key={`${row.jobId}-${row.jobNumber}`}
      sx={{
        alignItems: 'center',
        border: `1px solid ${COLORS.backgroundMain}`,
        borderRadius: '8px',
        display: 'flex',
        gap: 2,
        justifyContent: 'space-between',
        px: 1.5,
        py: 1,
      }}
    >
      <Box sx={{minWidth: 0}}>
        <Typography sx={{fontSize: '0.95rem', fontWeight: 600}}>
          {`Job ${row.jobNumber}`}
          {row.homeOwnerName ? ` - ${row.homeOwnerName}` : ''}
        </Typography>
        <Typography sx={{color: COLORS.fontPale, fontSize: '0.85rem', wordBreak: 'break-word'}}>
          {row.fullAddress || '(no address on file)'}
        </Typography>
        <Typography sx={{color: COLORS.fontPale, fontSize: '0.75rem'}}>
          {`Inspection: ${row.inspectionStatus || '-'}`}
          {row.aiStatus ? ` | AI: ${row.aiStatus}` : ''}
          {row.acceptAiCall ? '' : ' | not AI-enabled'}
        </Typography>
      </Box>
      <Button
        variant={'contained'}
        size={'small'}
        disabled={linkingJobNumber !== null}
        onClick={() => handleLink(row)}
        sx={{backgroundColor: COLORS.globalBlue, flexShrink: 0, fontWeight: 600}}
      >
        {linkingJobNumber === row.jobNumber ? 'Linking...' : 'Link'}
      </Button>
    </Box>
  );

  const aiSummary = describeAiAnswer(call?.answer);
  const callerDetails = [call?.homeOwnerName, call?.homeOwnerPhone].filter(Boolean).join(' · ');

  return (
    <ConfirmModal open={open} handleClose={onClose} hideHeader width={{sx: '98%', md: '720px'}}>
      <Box px={{xs: 1, md: 2}}>
        <Typography sx={{fontSize: '1.15rem', fontWeight: 700, mb: 0.5, textAlign: 'center'}}>
          Find the inspection for this call
        </Typography>
        <Typography sx={{color: COLORS.fontPale, fontSize: '0.9rem', mb: 2, textAlign: 'center'}}>
          {call?.direction === 'inbound'
            ? 'This caller rang in and the AI could not match them to a job.'
            : 'The AI could not match this call to a job.'}
        </Typography>

        {(callerDetails || aiSummary) && (
          <Box
            sx={{
              backgroundColor: COLORS.backgroundMain,
              borderRadius: '8px',
              mb: 3,
              px: 2,
              py: 1.5,
            }}
          >
            {callerDetails && (
              <Typography sx={{fontSize: '0.95rem', fontWeight: 600}}>
                {`Caller: ${callerDetails}`}
              </Typography>
            )}
            {aiSummary && (
              <Typography sx={{color: COLORS.fontPale, fontSize: '0.85rem', mt: callerDetails ? 0.5 : 0}}>
                {aiSummary}
              </Typography>
            )}
          </Box>
        )}

        {/* The theme pins shrunk MUI labels inside the box, so TextField's outlined label lands on
            top of the seeded address. The portal's own floating-label input avoids that. */}
        <FormControl variant={'standard'} fullWidth sx={{mb: 2}}>
          <FloatingLabel apiName={'ai-call-inspection-search'} label={'Job number or address'} />
          <CustomInput
            id={'ai-call-inspection-search'}
            fullWidth
            placeholder={'e.g. 12345 or 103 Main St'}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </FormControl>

        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '340px', overflowY: 'auto'}}>
          {searching && (
            <Box sx={{alignItems: 'center', display: 'flex', gap: 1, py: 1}}>
              <CircularProgress size={18} />
              <Typography sx={{fontSize: '0.9rem'}}>Searching...</Typography>
            </Box>
          )}
          {!searching && query.trim().length < MIN_QUERY_LENGTH && (
            <Typography sx={{color: COLORS.fontPale, fontSize: '0.9rem'}}>
              {`Type at least ${MIN_QUERY_LENGTH} characters.`}
            </Typography>
          )}
          {!searching && hasSearched && results.length === 0 && (
            <Box sx={{py: 1}}>
              <Typography sx={{fontSize: '0.9rem', fontWeight: 600}}>
                No inspection matches this search.
              </Typography>
              <Typography sx={{color: COLORS.fontPale, fontSize: '0.85rem', mt: 0.5}}>
                Try the job number, or just the street number and name. If you close this, the call
                stays recorded as unmatched and you can link it later from Unmatched Calls.
              </Typography>
            </Box>
          )}
          {!searching && results.map(renderResult)}
        </Box>

        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
          <Button variant={'outlined'} size={'medium'} onClick={onClose} sx={{fontWeight: 600}}>
            Close
          </Button>
        </Box>
      </Box>
    </ConfirmModal>
  );
};

AiCallInspectionSearchDialog.propTypes = {
  open: PropTypes.bool,
  /** The accepted call: `aiCallId`, `address`, `answer`, `direction`, homeowner details. */
  call: PropTypes.instanceOf(Object),
  onClose: PropTypes.func.isRequired,
};

AiCallInspectionSearchDialog.defaultProps = {
  open: false,
  call: null,
};

export default AiCallInspectionSearchDialog;
